<template>
  <!-- eslint-disable -->
  <PinturaEditor
    id="step1"
    ref="pinturaEditor"
    v-bind="editorDefaults"
    :src="src"
    :util="util"
    :imageAnnotation="positionAreaAnnotation"
    :imageDecoration="activePositionShapes"
    :preview-upscale="true"
    :beforeUpdateShape="beforeUpdateShape"
    :beforeSelectShape="beforeSelectShape"
    :beforeDeselectShape="beforeDeselectShape"
    :beforeRemoveShape="beforeRemoveShape"
    :zoomLevel="zoomLevel"
    @pintura:updateshape="updateShape"
    @pintura:zoom="(e) => $emit('action:zoom', e)"
    @pintura:load="(e) => $emit('action:load')"
    @select-shape="selectShape"
    @pintura:removeshape="onRemoveShape"
    @mouseup.native="resetInteractionTypeHandler"
    @touchend.native="resetInteractionTypeHandler"
  />
</template>
<script>
import Vue from 'vue';

import { limitToContextWithCorrectionFactor } from '@/utils/common';
import {
  /**
   * Import the default image reader and writer
   */
  createDefaultImageReader,
  createDefaultImageWriter,

  /**
   * The method used to register the plugins
   */
  setPlugins,

  /**
   * The plugins we want to use
   */
  plugin_decorate,
  plugin_crop,
  plugin_annotate,

  /**
   * The user interface and plugin locale objects
   */
  locale_en_gb,
  plugin_decorate_locale_en_gb,

  /**
   * Because we use the annotate plugin we also need to import the markup editor
   * locale
   */
  markup_editor_locale_en_gb,

  /**
   * Import the default configuration for the markup editor and finetune
   * plugins markup_editor_defaults,
   */
  findNode,
  colorStringToColorArray,
  createNode,
  appendNode,
} from 'pintura';

import { PinturaEditor } from '@/components/w2pPintura/PinturaEditor';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { mapState, mapActions } from 'pinia';
import { lineWidthV2 } from '@/constants/areaLineWidth';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import cloneDeep from 'lodash.clonedeep';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { Category } from '@midocean/webapps-shared-domain';
import {
  generateTargetCoordinates,
  getActivePanelNumber,
} from '@/utils/drawPanelLines';
import {
  drawPanelSections,
  generatePanelSections,
  updateTargetLines,
} from '@/utils/roundItemsHelper';
import {
  getShapePositionInAreainPtV2,
  getShapePositionOnAreaInMM,
} from '@/utils/areaHelper';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import {
  CONTEXT_NODES,
  ACTION_BUTTON_NODES,
} from './PinturaControls/contextControls';
import { RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE } from '@/constants/featureFlags';
import { BACKGROUND_IMAGE_W } from '../../constants';

setPlugins(plugin_decorate, plugin_crop, plugin_annotate);

const ROTATING_SHAPE_INFO_LABEL_WIDTH = 80;
const NARROW_SHAPE_INFO_LABEL_WIDTH = 180;
const DEFAULT_SHAPE_INFO_LABEL_WIDTH = 180;
const INFO_LABEL_X_OFFSET = 20;
const INFO_LABEL_Y_OFFSET = 20;

export default Vue.extend({
  name: 'W2pPintura',
  components: {
    PinturaEditor,
  },
  props: {
    zoomLevel: {
      type: Number,
      required: false,
      default: undefined,
    },
    activeInteractionMode: {
      type: String,
      required: false,
      default: 'auto',
    },
  },
  data() {
    return {
      src: require('../../../../assets/resizedCheckerTransparency.png'),
      util: 'finetune',
      // Our editor custom configuration options
      editorDefaults: {
        imageReader: createDefaultImageReader(),
        imageWriter: createDefaultImageWriter(),
        stickerEnableSelectImage: false,
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        annotateActiveTool: 'rectangle',
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        utils: ['decorate'],
        decorateTools: [],
        decorateActiveTool: 'move',
        enableToolbar: false,
        enableButtonExport: false,
        enableButtonRevert: false,
        enableNavigateHistory: false,
        // enableZoomControls: false,
        enableZoom: true,

        // The icons and labels to use in the user interface (required)
        locale: {
          ...locale_en_gb,
          ...plugin_decorate_locale_en_gb,
          ...markup_editor_locale_en_gb,
        },
        shapeInfoLabel__visible: false,
        shapeInfoLabel__positionText: '',
        shapeInfoLabel__width: DEFAULT_SHAPE_INFO_LABEL_WIDTH,
        shapeInfoLabel__height: 28,
        shapeInfoLabel__xOffset: 0,
        shapeInfoLabel__yOffset: 0,
        shapeInfoLabel__rotation: '0rad',
        willRenderShapeControls: (controls, _selectedShapeId) => {
          const alphaNode = findNode('alpha', controls);
          const betaNode = findNode('beta', controls);

          // Hide the default shape controls from alphaNode
          alphaNode[3].forEach((toolButton, index) => {
            alphaNode[3][index] = false;
          });

          // Create custom nodes for betaNode (shape controls) based on CONTEXT_NODES
          let customNodes = [];
          const activeShape = this.shapeById(this.activeShapeId);
          if (!activeShape.isInArea) {
            return;
          }

          let buttonsToAdd = [];
          if (
            RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE &&
            this.activePosition.anchorPoint
          ) {
            buttonsToAdd = CONTEXT_NODES;
          }

          buttonsToAdd = buttonsToAdd.concat(
            ACTION_BUTTON_NODES.map((node) => {
              return {
                ...node,
                label: this.$t(node.label),
              };
            })
          );

          customNodes = buttonsToAdd.map((node) => {
            return createNode('Button', node.id, {
              label: node.label, // TODO: Add as a translation
              class: node.class,
              hideLabel: node.hideLabel,
              icon: node.icon,
              onclick: () => {
                if (node.id === 'custom-recommended-print-position') {
                  this.$emit('recommended-print-position');
                } else if (
                  node.id === 'custom-recommended-print-position-info'
                ) {
                  this.$emit('recommended-print-position-info');
                } else if (node.id === 'custom-delete-shape') {
                  const activeShape = this.shapeById(this.activeShapeId);
                  this.onRemoveShape(activeShape);
                }
              },
            });
          });

          // Append custom nodes to betaNode (shape controls)
          customNodes.forEach((node) => appendNode(node, betaNode));

          // Hide the default shape controls from betaNode
          betaNode[3].forEach((toolButton, index) => {
            const foundCustomNode = customNodes.find(
              (node) => node[1] === toolButton[1]
            );
            if (foundCustomNode) return;

            betaNode[3][index] = false;
          });

          return controls;
        },
        willRenderCanvas: (shapes, state) => {
          const { selectionRect, isInteracting } = state;

          this.setBorderStroke(shapes);
          this.updateShapeInfoLabel(shapes, isInteracting);

          if (this.selectedPrintPosition.id) {
            this.$emit('pintura:selection', selectionRect);
          }
          return shapes;
        },
      },
      shapeInteractionType: 'NONE',
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'activePosition',
      'selectedPrintPosition',
      'getProductPrintPosition',
      'setPanelPosition',
      'centerCoordinatesOfPanels',
      'panelsPositions',
      'isSublimationTechnique',
      'printPositionScaleFactor',
    ]),
    ...mapState(useShapeStore, [
      'positionShapes',
      'shapes',
      'shapeById',
      'activeShapeId',
    ]),
    ...mapState(useMirrorStore, ['getActiveMirrorShapes']),
    ...mapState(useHistoryStore, ['isUndoPossible', 'isRedoPossible']),

    positionAreaAnnotation() {
      if (this.activePosition && this.activePosition.printArea) {
        if (this.activePosition.printArea.shapeType === 'Rectangle') {
          const {
            x,
            y,
            width,
            height,
            rotation,
            isLanyard,
            distanceOfMirrorPx,
          } = this.printPositionArea(this.activePosition.id, true);
          const annotationShapes = [];

          const rectLineWidth = (lineWidthV2 * BACKGROUND_IMAGE_W) / 100;
          const xWithLine = x - rectLineWidth / 2;
          const yWithLine = y - rectLineWidth / 2;
          const widthWithLine = width + rectLineWidth;
          const heightWithLine = height + rectLineWidth;

          if (isLanyard) {
            annotationShapes.push(
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x: xWithLine,
                y: yWithLine,
                width: widthWithLine,
                height: heightWithLine,
              },
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x: xWithLine,
                y: yWithLine + distanceOfMirrorPx,
                width: widthWithLine,
                height: heightWithLine,
              }
            );
          } else if (this.activePosition.category === Category.RoundItem) {
            annotationShapes.push(
              ...drawPanelSections(this.activePosition, y, height, x)
            );
            this.centerCoordinatesOfPanels.forEach((element) => {
              annotationShapes.push(element);
            });

            return [
              ...annotationShapes,
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x: xWithLine,
                y: yWithLine,
                width: widthWithLine,
                height: heightWithLine,
                dashedLine: [10, 10],
                lineDash: [10, 10],
              },
            ];
          } else {
            const printPosition = {
              id: this.activePosition.id,
              x: xWithLine,
              y: yWithLine,
              width: widthWithLine,
              height: heightWithLine,
              rotation,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidthV2}%`,
            };

            annotationShapes.push(printPosition);
          }

          return annotationShapes;
        }

        if (this.activePosition.printArea.shapeType === 'Ellipse') {
          const { x, y, rx, ry, rotation } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidthV2}%`,
              rotation,
              x,
              y,
              rx,
              ry,
            },
          ];
        }

        if (this.activePosition.printArea.shapeType === 'Polygon') {
          const { points } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidthV2}%`,
              rotation: 0,
              points,
            },
          ];
        }
      }

      return [];
    },

    activePositionShapes() {
      if (this.activePosition && this.activePosition.id) {
        return [
          ...this.positionShapes(this.activePosition.id),
          ...this.getActiveMirrorShapes(),
        ];
      } else {
        return [];
      }
    },
  },

  watch: {
    activeInteractionMode(newValue) {
      if (newValue === 'pan') {
        this.pintura.editor.markupEditorInteractionMode = 'pan';
      } else {
        this.pintura.editor.markupEditorInteractionMode = 'auto';
      }
    },
    activePosition() {
      if (!this.activePosition || !this.activePosition.id) return;

      const { x, y, width, height } = this.printPositionArea(
        this.activePosition.id,
        true
      );
      this.setPanelsPositions([...generatePanelSections(this.activePosition)]);
      for (let index = 0; index < this.panelsPositions.length; index++) {
        if (this.panelsPositions[index]) {
          this.setCenterCoordinatesOfPanels([
            ...this.centerCoordinatesOfPanels,
            ...generateTargetCoordinates(
              this.panelsPositions,
              index,
              height,
              y
            ),
          ]);
        }
      }
    },
  },

  created() {
    this.$nextTick(() => {
      window.pintura = this.$refs['pinturaEditor'];
      this.pintura = window.pintura;
    });
    /**
     * Clean up editor instance to prevent memory leaks
     */
    this.$once('hook:beforeDestroy', () => {
      window.pintura = undefined;
      this.pintura = undefined;
    });
  },

  methods: {
    ...mapActions(useProductStore, [
      'printPositionArea',
      'updateProductStateFromSnapshot',
      'area',
      'setCenterCoordinatesOfPanels',
      'setPanelsPositions',
    ]),
    ...mapActions(useShapeStore, [
      'updateShapePosition',
      'deselectShape',
      'checkIsShapeInArea',
      'updateShapeStateFromSnapshot',
      'setSmallDetailsCheckToInvalid2',
      'applySublimationConverter',
      'removeShape',
    ]),
    ...mapActions(useNotificationStore, [
      'runValidators',
      'updateNotificationStateFromSnapshot',
    ]),
    ...mapActions(useHistoryStore, ['undo', 'redo', 'recordHistory']),
    ...mapActions(useMirrorStore, [
      'updateMirrorStateFromSnapshot',
      'updateMirrorShape',
      'removeMirrorShape',
    ]),

    updateShape(current) {
      /**
       * UPDATE shape x, y, rotation
       */
      if (this.activePosition.id) {
        const scaledArea = this.printPositionArea(this.activePosition.id, true);
        const productArea = this.activePosition.printArea;

        this.updateShapePosition(
          { ...current, isOnRecommendedPrintPosition: false },
          scaledArea,
          productArea,
          false,
          true
        );
        this.checkIsShapeInArea(current.id);
        this.runValidators();

        const activeShape = this.shapeById(this.activeShapeId);
        if (
          this.isSublimationTechnique &&
          activeShape &&
          activeShape.isInArea
        ) {
          this.applySublimationConverter(current.id);
        }

        if (activeShape) {
          this.updateMirrorShape(activeShape);
        }

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      }
    },

    selectShape(id) {
      this.shapes[0].isSelected = true;
    },

    onRemoveShape(shape) {
      if (shape) {
        updateTargetLines(
          shape,
          this.centerCoordinatesOfPanels,
          this.panelsPositions,
          true
        );
      }
      this.removeMirrorShape(shape.id);
      this.removeShape(shape.id);
      this.runValidators();

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    beforeSelectShape(current, target) {
      this.$emit('beforeselect-shape', target);
      return true;
    },

    beforeUpdateShape(shape, props, ctx) {
      if (
        shape.smallDetailsResult &&
        (props.width !== undefined || props.height !== undefined)
      ) {
        this.setSmallDetailsCheckToInvalid2(shape.id);
      }

      this.shapeInteractionType = this.getShapeInteractionType(props);

      updateTargetLines(
        shape,
        this.centerCoordinatesOfPanels,
        this.panelsPositions
      );

      const activePanelNumber = getActivePanelNumber(
        shape.x,
        this.panelsPositions
      );

      const panelX = this.panelsPositions[activePanelNumber - 1];
      const nextPanelPosition = this.panelsPositions[activePanelNumber];
      this.setPanelPosition({
        x: panelX,
        width: nextPanelPosition - panelX,
      });

      if (props.width !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.width = props.width < 1 ? 1 : props.width;
        props.height = props.width === 1 ? shape.height : props.height;
      }

      if (props.height !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.height = props.height < 1 ? 1 : props.height;
        props.width = props.height === 1 ? shape.width : props.width;
      }

      const limit = limitToContextWithCorrectionFactor(
        ctx,
        undefined, // use default offset
        props,
        shape
      );

      /**app
       * *** SPECIAL: In case we get a rotation property inside props -
       * Here x and y aren't set at all, so we need to return the orginal back
       * without any modification
       *
       * VERY IMPORTANT:
       * Bear in mind props will be of a different shape, depending on action
       * being performed
       */
      const rotation = ((shape.rotation * 180) / Math.PI) % 360;
      if ((rotation !== 0 && rotation !== 180) || props.rotation) {
        return props;
      }

      this.$emit('beforeupdate-shape', shape);

      /**
       * Otherwise apply limitation to x and y
       * cases like scaling and moving operations
       */
      return {
        ...props,
        x: limit.x,
        y: limit.y,
      };
    },

    beforeDeselectShape(current, target) {
      this.deselectShape();
      return true;
    },

    beforeRemoveShape(shape) {
      return true;
    },

    undoHandler() {
      const stateSnapshot = this.undo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayloadV2();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    redoHandler() {
      const stateSnapshot = this.redo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayloadV2();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    setBorderStroke(shapes) {
      shapes.interfaceShapes.map((shape) => {
        if (shape.id === 'markup-manipulator-segment-decorate') {
          shape.strokeColor = [0.1412, 0.6314, 0.2824];
        }
      });
    },

    resetInteractionTypeHandler() {
      const shapes = { decorationShapes: [] };
      this.updateShapeInfoLabel(shapes, false);
    },

    updateShapeInfoLabel(shapes, isInteracting) {
      const interactionType = this.shapeInteractionType;
      const selectedShape = shapes.decorationShapes.find(
        (shape) => shape.isSelected
      );
      const isSelectedShape = selectedShape !== undefined;

      if (!isInteracting || !isSelectedShape) {
        if (this.shapeInfoLabel__visible) {
          this.shapeInfoLabel__visible = false;
          this.setShapeInfoLabelAttributes();
        }
        return;
      }

      this.shapeInfoLabel__visible = true;
      this.shapeInfoLabel__width = this.getShapeInfoLabelWidth(interactionType);
      const { infoLabelXOffset, infoLabelYOffset } =
        this.getShapeInfoLabelPostion(shapes, selectedShape, interactionType);
      this.shapeInfoLabel__positionText = this.getShapeInfoLabelText(
        selectedShape,
        interactionType
      );

      const isOffsetChanged =
        Math.abs(this.shapeInfoLabel__xOffset - infoLabelXOffset) > 0.1 ||
        Math.abs(this.shapeInfoLabel__yOffset !== infoLabelYOffset) > 0.1;

      if (!(isOffsetChanged || isInteracting)) {
        return;
      }

      this.shapeInfoLabel__xOffset = infoLabelXOffset;
      this.shapeInfoLabel__yOffset = infoLabelYOffset;

      this.setShapeInfoLabelAttributes();
    },

    getShapeInteractionType(updatingProps) {
      if (updatingProps.rotation !== undefined) {
        return 'ROTATING';
      }

      if (
        updatingProps.width !== undefined ||
        updatingProps.height !== undefined
      ) {
        return 'RESIZING';
      }

      if (updatingProps.x !== undefined || updatingProps.y !== undefined) {
        return 'MOVING';
      }

      return 'NONE';
    },

    getShapeInfoLabelText(selectedShape, interactionType) {
      let newPosition;
      let isLanyard = this.activePosition.category === 'Lanyard';
      let distanceOfMirrorPosition = this.activePosition.categoryConfiguration
        ?.distanceOfMirrorPosition
        ? this.activePosition.categoryConfiguration?.distanceOfMirrorPosition
        : 0;
      switch (interactionType) {
        case 'RESIZING':
        case 'MOVING': {
          const scaledArea = this.printPositionArea(
            this.activePosition.id,
            true
          );
          const productArea = this.activePosition.printArea;
          newPosition = this.getShapePosition(
            { ...selectedShape, isOnRecommendedPrintPosition: false },
            scaledArea,
            productArea
          );
        }
      }
      switch (interactionType) {
        case 'ROTATING': {
          let angle = ((selectedShape.rotation * 180) / Math.PI).toFixed(0);
          if (angle > 180) {
            angle = angle - 360;
          }
          if (angle.toString() === '-0') {
            angle = 0;
          }
          return `${angle}°`;
        }
        case 'MOVING': {
          if (newPosition) {
            const x = newPosition.xOnAreaMM.toFixed(0);
            let y = newPosition.yOnAreaMM.toFixed(0);
            if (
              isLanyard &&
              distanceOfMirrorPosition > 0 &&
              y > distanceOfMirrorPosition
            ) {
              y = (y - distanceOfMirrorPosition).toFixed(0);
            }
            return `X: ${x}mm Y: ${y}mm`;
          }
          return '';
        }
        case 'RESIZING': {
          if (newPosition) {
            return `${newPosition.widthOnAreaMM.toFixed(
              0
            )}mm x ${newPosition.heightOnAreaMM.toFixed(0)}mm`;
          }
          return '';
        }
        default: {
          return '';
        }
      }
    },

    getShapePosition(shape, scaledArea, productPrintArea) {
      const originalShapeIdx = this.shapes.findIndex((x) => x.id === shape.id);
      if (originalShapeIdx !== -1) {
        let adjustedShapePosition = {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
          rotation: shape.rotation,
        };

        const boundingBox = this.getAreaBoundingBox(scaledArea);
        if (adjustedShapePosition.rotation !== 0) {
          const centerX =
            adjustedShapePosition.x + adjustedShapePosition.width / 2;
          const centerY =
            adjustedShapePosition.y + adjustedShapePosition.height / 2;

          const cos = Math.cos(adjustedShapePosition.rotation);
          const sin = Math.sin(adjustedShapePosition.rotation);

          const dx = adjustedShapePosition.x - centerX;
          const dy = adjustedShapePosition.y - centerY;

          adjustedShapePosition = {
            ...adjustedShapePosition,
            x: centerX + (dx * cos - dy * sin),
            y: centerY + (dx * sin + dy * cos),
          };
        }
        if (boundingBox.rotation !== 0) {
          const centerX = boundingBox.x + boundingBox.width / 2;
          const centerY = boundingBox.y + boundingBox.height / 2;
          const cos = Math.cos(-boundingBox.rotation);
          const sin = Math.sin(-boundingBox.rotation);

          const dx = adjustedShapePosition.x - centerX;
          const dy = adjustedShapePosition.y - centerY;

          adjustedShapePosition = {
            ...adjustedShapePosition,
            x: centerX + (dx * cos - dy * sin),
            y: centerY + (dx * sin + dy * cos),
          };
        }

        const positionOnAreaInPt = getShapePositionInAreainPtV2(
          adjustedShapePosition,
          boundingBox
        );

        if (positionOnAreaInPt) {
          const positionOnAreaInMM = getShapePositionOnAreaInMM(
            positionOnAreaInPt,
            productPrintArea,
            adjustedShapePosition.width / adjustedShapePosition.height
          );

          if (positionOnAreaInMM) {
            return {
              x: positionOnAreaInPt.x,
              y: positionOnAreaInPt.y,
              width: positionOnAreaInPt.width,
              height: positionOnAreaInPt.height,
              xOnAreaMM: positionOnAreaInMM.xOnAreaMM,
              yOnAreaMM: positionOnAreaInMM.yOnAreaMM,
              widthOnAreaMM: positionOnAreaInMM.widthOnAreaMM,
              heightOnAreaMM: positionOnAreaInMM.heightOnAreaMM,
            };
          }
        }
      }
    },

    getAreaBoundingBox(scaledArea) {
      switch (scaledArea.kind) {
        case 'Polygon': {
          const xs = scaledArea.points.map((point) => point.x);
          const ys = scaledArea.points.map((point) => point.y);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const minY = Math.min(...ys);
          const maxY = Math.max(...ys);
          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            kind: 'Rectangle',
            isLanyard: false,
            rotation: 0,
            scaleFactor: scaledArea.scaleFactor,
          };
        }
        case 'Ellipse':
          return {
            x: scaledArea.x - scaledArea.rx,
            y: scaledArea.y - scaledArea.ry,
            width: scaledArea.rx * 2,
            height: scaledArea.ry * 2,
            kind: 'Rectangle',
            isLanyard: false,
            rotation: scaledArea.rotation,
            scaleFactor: scaledArea.scaleFactor,
          };
        case 'Rectangle':
        default:
          return scaledArea;
      }
    },

    getShapeInfoLabelPostion(shapes, selectedShape) {
      const borderShapes = shapes.interfaceShapes.filter(
        (shape) => shape.id === 'markup-manipulator-segment-decorate'
      );
      if (borderShapes.length === 0) {
        return { infoLabelXOffset, infoLabelYOffset };
      }

      const points = borderShapes[2].points;
      let { x: infoLabelXOffset, y: infoLabelYOffset } = this.rotatePointBack(
        points,
        selectedShape.rotation
      );

      infoLabelXOffset += INFO_LABEL_X_OFFSET;
      infoLabelYOffset += INFO_LABEL_Y_OFFSET;

      return { infoLabelXOffset, infoLabelYOffset };
    },

    rotatePointBack(points, alpha) {
      const topLeftX = points[0].x;
      const topLeftY = points[0].y;
      const x = points[2].x;
      const y = points[2].y;
      const cx = (points[0].x + points[2].x) / 2;
      const cy = (points[0].y + points[2].y) / 2;
      const cos = Math.cos(alpha);
      const sin = Math.sin(alpha);

      const dx = x - cx;
      const dy = y - cy;

      const x0 = dx * cos + dy * sin;
      const y0 = -dx * sin + dy * cos;

      const newX = cx + x0;
      const newY = cy + y0;

      const offsetX = newX - topLeftX;
      const offsetY = newY - topLeftY;
      return {
        x: offsetX,
        y: offsetY,
      };
    },

    getShapeInfoLabelWidth(interactionType) {
      switch (interactionType) {
        case 'ROTATING': {
          return ROTATING_SHAPE_INFO_LABEL_WIDTH;
        }
        case 'MOVING':
        case 'RESIZING': {
          return NARROW_SHAPE_INFO_LABEL_WIDTH;
        }
        default: {
          return DEFAULT_SHAPE_INFO_LABEL_WIDTH;
        }
      }
    },

    setShapeInfoLabelAttributes() {
      const root = this.$el;
      if (!root) {
        return;
      }

      root.setAttribute(
        'style',
        `--shape-info-visibility: ${
          this.shapeInfoLabel__visible ? 'block' : 'none'
        };\
--shape-info-positiondata: '${this.shapeInfoLabel__positionText}';\
--shape-info-width: ${this.shapeInfoLabel__width}px;\
--shape-info-height: ${this.shapeInfoLabel__height}px;\
--shape-info-top: ${this.shapeInfoLabel__yOffset}px;\
--shape-info-left: ${this.shapeInfoLabel__xOffset}px;\
--shape-info-rotation: ${this.shapeInfoLabel__rotation};`
      );
    },
  },
});
</script>

<style lang="scss">
@import '../../../../styles/_carbon-utils';
@import '../../../../styles/carbon-overrides.scss';
@import '../../../../../node_modules/pintura/pintura.css';

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,700;1,400&display=swap');

#root {
  position: relative;
  height: 100%;

  #header {
    position: relative;
  }
}

.pintura-editor {
  --color-background: 244, 244, 244;
  --font-family: 'Roboto', sans-serif;
  --font-size: 16px;
  --color-secondary: #24a148;
}

.PinturaRoot {
  padding-top: 0;
  background: #e0e0e0 !important ;
}

.PinturaRoot[data-env~='landscape'] > .PinturaNavTools {
  justify-content: left;
}

.CustomTabList {
  display: none;
  background: #f4f4f4;

  .PinturaButton {
    width: 3rem;
    height: 3rem;
    border: none;
    padding: 0.1rem;
    margin: 0;
  }
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavMain {
  display: none;
}
.PinturaRoot .PinturaEditorOverlay {
  background-color: rgba(0, 0, 0, 0.6);
}
.custom-shape-control.PinturaButtonIconOnly {
  width: 2.3em;
  height: 2.3em;
  svg {
    fill: white;
  }
}
.custom-bring-to-front.PinturaButtonIconOnly {
  svg {
    width: 80%;
  }
}

.import-wizard-v2 .PinturaUtilMain {
  padding-top: 80px;
}

.import-wizard-v2 .PinturaRoot .PinturaNavGroup {
  margin-top: 20px;
}

.PinturaShapeControlsGroup {
  box-shadow: none;
  border-radius: 0;
  border: 1px solid #e1dbdb;
  border: 0;
  border-radius: 0;
}

.recommended-position-command {
  display: flex;
  align-items: center;
  gap: 5px;
}

.PinturaButton > span {
  position: relative;
}

.PinturaShapeControlsGroup .PinturaButtonIconOnly {
  width: fit-content;
}

.PinturaShapeControlsGroup button {
  background-color: white;
  width: fit-content;
  color: black;
  font-weight: 400;
  border-radius: 0 !important;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  box-shadow: none;
}

.PinturaShapeControlsGroup button:first-child {
  border-right: 0;
}

.PinturaShapeControlsGroup
  button:first-child
  .PinturaButtonInner
  .PinturaButtonIcon {
  position: absolute;
  top: 0;
  left: 10px;
}

.PinturaShapeControlsGroup
  button.relative:first-child
  .PinturaButtonInner
  .PinturaButtonIcon {
  position: relative;
  top: 5px;
  left: 4px;
}

.PinturaShapeControlsGroup
  button:nth-child(2)
  .PinturaButtonInner
  .PinturaButtonIcon {
  position: relative;
  top: 4px;
  left: 3px;
}

.PinturaShapeControlsGroup
  button:nth-child(3)
  .PinturaButtonInner
  .PinturaButtonIcon {
  position: relative;
  top: 3px;
  left: 5px;
}

.PinturaShapeControlsGroup button:hover {
  background-color: #f4f4f4 !important;
}

.PinturaShapeControlsGroup > .PinturaButton .PinturaButtonLabel {
  margin-left: 20px;
  font-weight: normal;
}

.PinturaShapeControls {
  top: 10px;
}

.PinturaShapeManipulator[data-control='point']:first-of-type::before {
  display: var(--shape-info-visibility);
  content: var(--shape-info-positiondata);
  width: var(--shape-info-width);
  height: var(--shape-info-height);
  font-family: var(--font-family);
  font-size: 1em;
  font-weight: 400;
  padding: 0.5em;
  text-align: center;
  position: relative;
  background-color: #000000;
  color: #ffffff;
  box-shadow: 0.125em 0.25em rgba(0, 0, 0, 0.25);
  border-radius: 0.25em;
  box-sizing: border-box;
  top: var(--shape-info-top);
  left: var(--shape-info-left);
}

.PinturaShapeManipulator[data-control='point'],
.PinturaShapeManipulator[data-control='scale'] {
  background: #ffffff;
  border-radius: 0em;
  border: 1px solid #24a148;
}

div.PinturaShapeManipulator[data-control='point']:hover {
  background-color: #198038;
}

.PinturaShapeManipulator[data-control='rotate'] {
  background: #ffffff;
  border: 1px solid #24a148;
  box-shadow: none;
}

.PinturaShapeManipulator[data-control='rotate']:hover {
  background: #198038;
  border: 1px solid #24a148;
}

.PinturaShapeEditor
  div.PinturaShapeManipulator[data-control='point'][style*='cursor: nwse-resize'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.5 0.5V7.29004L1.35352 6.4375L2.83398 4.95703L4.95898 7.08398V7.08496L9.04199 11.165L7.56348 12.6445L6.70605 13.502L7.91895 13.498L12.9844 13.4824L13.4814 13.4805L13.4834 12.9834L13.4971 7.91699L13.5 6.70508L12.6436 7.5625L11.1514 9.05371L7.06836 4.97461L4.94238 2.84766L7.29004 0.5H0.5Z" fill="black" stroke="white"/></svg>')
      7 7,
    auto !important;
}

.PinturaShapeEditor
  div.PinturaShapeManipulator[data-control='point'][style*='cursor: nesw-resize'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(90)"><path d="M0.5 0.5V7.29004L1.35352 6.4375L2.83398 4.95703L4.95898 7.08398V7.08496L9.04199 11.165L7.56348 12.6445L6.70605 13.502L7.91895 13.498L12.9844 13.4824L13.4814 13.4805L13.4834 12.9834L13.4971 7.91699L13.5 6.70508L12.6436 7.5625L11.1514 9.05371L7.06836 4.97461L4.94238 2.84766L7.29004 0.5H0.5Z" fill="black" stroke="white"></path></svg>')
      7 7,
    auto !important;
}

.PinturaShapeEditor div.PinturaShapeManipulator[data-control='rotate'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.422 8.92358L9.5161 12.3043C9.29517 13.1288 9.23917 13.9887 9.35058 14.835C9.462 15.6813 9.73806 16.4978 10.1649 17.237C10.5917 17.9762 11.1607 18.6235 11.8379 19.1432C12.5151 19.6628 13.2878 20.0442 14.1123 20.2652L17.493 21.171L16.663 24.2688L22.4279 20.9404L19.0995 15.1755L18.2695 18.2732L14.8888 17.3674C14.4448 17.2484 14.0282 17.0429 13.6636 16.7631C13.2991 16.4833 12.9936 16.1344 12.7639 15.7365C12.5341 15.3385 12.3847 14.8995 12.3246 14.444C12.2646 13.9883 12.2949 13.5247 12.4139 13.0808L13.3197 9.70003L16.4175 10.5301L13.0891 4.76516L7.32421 8.09354L10.422 8.92358Z" fill="black" stroke="white"/></svg>')
      15 15,
    auto !important;
}

.PinturaShapeEditor
  div.PinturaShapeManipulator[role='slider'][aria-orientation='horizontal'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(135)translate(4,4)"><path d="M0.5 0.5V7.29004L1.35352 6.4375L2.83398 4.95703L4.95898 7.08398V7.08496L9.04199 11.165L7.56348 12.6445L6.70605 13.502L7.91895 13.498L12.9844 13.4824L13.4814 13.4805L13.4834 12.9834L13.4971 7.91699L13.5 6.70508L12.6436 7.5625L11.1514 9.05371L7.06836 4.97461L4.94238 2.84766L7.29004 0.5H0.5Z" fill="black" stroke="white"></path></svg>')
      10 10,
    auto !important;
}

.PinturaShapeEditor
  div.PinturaShapeManipulator[role='slider'][aria-orientation='vertical'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(45)translate(2,5)"><path d="M0.5 0.5V7.29004L1.35352 6.4375L2.83398 4.95703L4.95898 7.08398V7.08496L9.04199 11.165L7.56348 12.6445L6.70605 13.502L7.91895 13.498L12.9844 13.4824L13.4814 13.4805L13.4834 12.9834L13.4971 7.91699L13.5 6.70508L12.6436 7.5625L11.1514 9.05371L7.06836 4.97461L4.94238 2.84766L7.29004 0.5H0.5Z" fill="black" stroke="white"></path></svg>')
      10 10,
    auto !important;
}

.PinturaUtilMain div[slot='main'][style*='cursor: move'] {
  cursor: url('data:image/svg+xml;utf8,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.3477 5.06177L15.1475 7.6311L15.5098 7.96313V7.89868L15.873 8.29712L16.0938 8.50024H16.0576L16.3789 8.85278L18.9395 11.6633L19.2461 12.0002L18.9395 12.3362L16.3789 15.1467L16.0566 15.5002H16.0869L15.9072 15.6633L15.5098 16.1008V16.0266L15.1367 16.3684L12.3271 18.9387L11.9893 19.2483L11.6514 18.9387L8.85156 16.3684L8.5 16.0452V16.0979L8.20312 15.7737L7.90527 15.5002H7.95312L7.63086 15.1477L5.06055 12.3372L4.75195 12.0002L5.06055 11.6624L7.63086 8.85278L7.95312 8.50024H7.91211L8.16406 8.2688L8.5 7.90259V7.96216L8.8623 7.6311L11.6719 5.06079L12.0107 4.7522L12.3477 5.06177ZM8.5 15.5002H10.5098V13.5002H8.5V15.5002ZM13.4893 15.5002H15.5098V13.5002H13.4893V15.5002ZM13.5 10.5198H15.5098V8.50024H13.5V10.5198ZM8.5 10.5198H10.5098V8.50024H8.5V10.5198Z" fill="black" stroke="white"/></svg>')
      12 12,
    auto !important;
}
</style>

<style lang="scss">
.pintura-sticker-editor {
  --editor-modal-overlay-opacity: 0.65;
  --editor-modal-border-radius: 1.5rem;
  --editor-max-width: 48%;
  --editor-max-height: 50%;
}
</style>
